import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM, ColorSystem } from "components/design/design-system";
import { Patient } from "common/models/types";
import Text from "components/atoms/Text";
import { useNavigate } from "react-router-dom";
import { calculateAge } from "utils/TimeUtil";
import { formatGender } from "utils/gender";

interface PatientItemProps {
  patient: Patient;
  isSelected: boolean;
  onClick: () => void;
}

const PatientItemContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ isSelected }) =>
    isSelected ? COLOR_SYSTEM.get("Gray")?.[50] : "#ffffff"};
  border: 1px solid
    ${({ isSelected }) =>
      isSelected ? COLOR_SYSTEM.get("Blue")?.[400] : COLOR_SYSTEM.get("Gray")?.[200]};

  &:hover {
    background-color: ${({ isSelected }) =>
      isSelected ? COLOR_SYSTEM.get("Gray")?.[50] : COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const PatientInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const PatientMeta = styled.div`
  display: flex;
  gap: 8px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-size: 12px;
`;

const PatientItem: React.FC<PatientItemProps> = ({ patient, isSelected, onClick }) => {
  const navigate = useNavigate();

  const age = calculateAge(patient.birthDate);
  const ageDisplay = age !== null ? `${age}세` : "";

  // 성별 정보 포맷팅 — 판정 불가 시 빈 문자열(구분자 `|`가 함께 사라진다)
  const genderDisplay = formatGender(patient.gender, "");

  return (
    <PatientItemContainer
      isSelected={isSelected}
      onClick={() => navigate(`/patients/${patient.id}`)}
    >
      <PatientInfo>
        <Text
          textType="Body1"
          color={isSelected ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[800]}
        >
          {/* 환자 번호가 있을 경우 표시, 없으면 '신규 환자'로 표시 */}
          {patient.id ? `환자 ${patient.id}` : "신규 환자"}
        </Text>
      </PatientInfo>
      <PatientMeta>
        <span>{ageDisplay}</span>
        {ageDisplay && genderDisplay && <span>|</span>}
        <span>{genderDisplay}</span>
        {(ageDisplay || genderDisplay) && patient.diseases && <span>|</span>}
        <span>{patient.diseases}</span>
      </PatientMeta>
    </PatientItemContainer>
  );
};

export default PatientItem;
